import { PageHeader, Section, Overline, PrimaryCTA, SecondaryCTA, TrustStrip } from "@/components/Section";
import { Database, Workflow, TrendingUp, FileCheck2 } from "lucide-react";

const LAYERS = [
  {
    n: "L00",
    icon: Database,
    title: "Evidence Base — the durable moat",
    desc: "The data-integration engine. Ingests, geocodes, standardises, and continuously refreshes public and local data. Every indicator carries full lineage: source, vintage, method, refresh cadence, basis flag. Built once per community; every module amortises against it.",
  },
  {
    n: "L01",
    icon: Workflow,
    title: "Diagnostic / Evaluation",
    desc: "Need, gaps, disparities, and system performance computed to defensible — and where applicable, regulated — methods. Each regulated method is a versioned module: same inputs + same version = same outputs, always. Suppression propagates; nothing is fabricated.",
  },
  {
    n: "L02",
    icon: TrendingUp,
    title: "Modelling",
    desc: "Projections and scenario testing beyond static snapshots: population trajectories, demand forecasting, what-if analysis for policy interventions.",
  },
  {
    n: "L03",
    icon: FileCheck2,
    title: "Reporting, Monitoring & Accountability",
    desc: "Compliant reports (DOCX, PPTX, PDF), dashboards, and longitudinal monitoring against adopted plans and targets. Named human sign-off on every published output — the layer that distinguishes Continuum from a data dump.",
  },
];

export default function Platform() {
  return (
    <>
      <PageHeader
        overline="The platform"
        title="One chassis. Four systems of care."
        lede="Continuum is a domain-agnostic evidence stack: the same four layers serve housing, mental health, aging and disability, and health care. What changes between domains is the indicators, the connectors, and the compliance templates that plug in — not the architecture."
        testid="platform-page-header"
      >
        <div className="flex flex-wrap gap-3 mt-10">
          <PrimaryCTA to="/contact" testid="platform-walkthrough-cta">Book a walkthrough</PrimaryCTA>
          <SecondaryCTA to="/method" testid="platform-method-cta">Read the method</SecondaryCTA>
        </div>
      </PageHeader>

      <Section testid="platform-architecture">
        <Overline className="mb-6">Architecture</Overline>
        <h2 className="text-3xl md:text-5xl font-black tracking-tighter mb-12 max-w-4xl">
          Four layers. One evidence base. <span className="text-[#FF8A00]">Every layer documented.</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-px bg-slate-200">
          {LAYERS.map((l, i) => {
            const Icon = l.icon;
            return (
              <div key={l.title} data-testid={`platform-layer-${i}`} className="bg-white p-8 hover:border-l-4 hover:border-[#FF8A00] hover:pl-7 transition-all duration-200 border-l-4 border-transparent">
                <div className="flex items-center justify-between mb-6">
                  <Icon className="text-[#FF8A00]" size={28} />
                  <span className="text-xs font-black tabular-nums text-slate-400">{l.n}</span>
                </div>
                <h3 className="text-xl font-black tracking-tight text-[#0A1224]">{l.title}</h3>
                <p className="text-slate-600 mt-3 leading-relaxed">{l.desc}</p>
              </div>
            );
          })}
        </div>
        <p data-testid="platform-priority-populations" className="text-slate-700 text-lg leading-relaxed mt-12 max-w-4xl border-l-2 border-[#FF8A00] pl-6">
          Priority populations — Indigenous peoples, seniors, persons with disabilities,
          veterans, women and gender-diverse persons, immigrants and refugees, Black and
          racially visible populations, 2SLGBTQI+ populations, people experiencing
          homelessness — are not separate modules. They are analytical dimensions that cut
          across every domain's evidence base, diagnostics, and reporting.
        </p>
      </Section>

      {/* SERVICE LANDSCAPE & FINANCIAL FLOWS — consolidates retired Community + Disparity pages */}
      <section id="service-landscape" data-testid="platform-service-landscape" className="bg-[#0A1224] text-white py-20 md:py-32 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Overline className="text-slate-400 mb-6">Cross-domain · Coming</Overline>
          <h2 className="text-3xl md:text-5xl font-black tracking-tighter leading-tight max-w-4xl">
            Who operates here, doing what, funded by whom —{" "}
            <span className="text-[#FF8A00]">and does it land where the need is?</span>
          </h2>
          <p className="text-slate-300 text-lg leading-relaxed mt-8 max-w-4xl">
            The Service Landscape layer is the capability with no apparent competitor. A
            geocoded registry of every agency, charity, and service active in a community —
            built from CRA T3010 filings, provincial registries, and program data — layered
            against place-based need indicators across all four domains. Funding-flow mapping
            shows where the money lands by geography, sector, and population. Disparity
            analysis computes funding per unit of measured need, exposing over- and
            under-served places. This layer is what makes Continuum a platform rather than
            four separate products.
          </p>

          <div className="grid lg:grid-cols-12 gap-px bg-white/10 mt-14">
            <div className="lg:col-span-5 bg-[#0A1224] p-8 border-l-4 border-[#FF8A00]">
              <Overline className="text-slate-400 mb-3">Hard rule</Overline>
              <p className="text-white font-bold text-xl leading-snug">
                "We will not publish a disparity number we cannot defend in a procurement
                review."
              </p>
              <p className="text-slate-300 mt-4 leading-relaxed">
                Every cell in the disparity matrix carries source, method, and refresh date.
                There is no "trust us." There is the lineage view.
              </p>
            </div>
            <div className="lg:col-span-7 bg-[#0A1224] p-8">
              <Overline className="text-slate-400 mb-3">A story we keep being asked to write</Overline>
              <p className="text-slate-300 leading-relaxed">
                A small arts-based mental-health agency has six weeks to submit a funding
                application. They know the need exists in their community. They cannot afford
                a consulting engagement to prove it. Continuum opens the service-landscape map
                for their region — every agency operating in mental health within a 25 km
                radius, the public funding that's already landed there, and the unmet-need
                gap. The application writes itself from the evidence. The decision — whether
                to apply, how to scope, what to claim — stays the agency's.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Section testid="platform-status" className="bg-[#050A15] text-white">
        <Overline className="text-slate-400 mb-6">Honest build status</Overline>
        <h2 className="text-3xl md:text-5xl font-black tracking-tighter mb-10 max-w-4xl">
          What's working now. What's coming next. <span className="text-[#FF8A00]">No fabricated screenshots.</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-px bg-white/10">
          <div className="bg-[#050A15] p-8">
            <div className="text-[#FF8A00] text-sm font-black tracking-[0.2em] uppercase">Live now</div>
            <ul className="mt-5 space-y-3 text-slate-300">
              <li className="pl-6 relative"><span className="absolute left-0 top-3 h-0.5 w-3 bg-[#FF8A00]" />HNA Engine — BC HNR + federal HICC from one engine</li>
              <li className="pl-6 relative"><span className="absolute left-0 top-3 h-0.5 w-3 bg-[#FF8A00]" />RoCA Benchmark reports · Community Housing Snapshot · Council Pack</li>
              <li className="pl-6 relative"><span className="absolute left-0 top-3 h-0.5 w-3 bg-[#FF8A00]" />Ask Continuum — AI assistant answering only from lineage-backed tenant data</li>
              <li className="pl-6 relative"><span className="absolute left-0 top-3 h-0.5 w-3 bg-[#FF8A00]" />Live municipal tenants, named sign-off</li>
            </ul>
          </div>
          <div className="bg-[#050A15] p-8">
            <div className="text-[#FF8A00] text-sm font-black tracking-[0.2em] uppercase">Next</div>
            <ul className="mt-5 space-y-3 text-slate-300">
              <li className="pl-6 relative"><span className="absolute left-0 top-3 h-0.5 w-3 bg-[#FF8A00]" />Homelessness System Intelligence &amp; Non-Market Inventory (Domain 1 deepening)</li>
              <li className="pl-6 relative"><span className="absolute left-0 top-3 h-0.5 w-3 bg-[#FF8A00]" />Mental health / crisis community profiles (Domain 2)</li>
              <li className="pl-6 relative"><span className="absolute left-0 top-3 h-0.5 w-3 bg-[#FF8A00]" />Seniors &amp; continuing-care intelligence (Domain 3)</li>
              <li className="pl-6 relative"><span className="absolute left-0 top-3 h-0.5 w-3 bg-[#FF8A00]" />Service Landscape &amp; Financial Flows (cross-domain)</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section testid="platform-trust" className="!py-16"><TrustStrip /></Section>
    </>
  );
}
